import { useConditionalSnackBar } from 'context/snackbar';
import { useFormik } from 'formik';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Template } from 'crono-fe-common/types/template';
import { initialValues, TemplateInputs, validationSchema } from './model';
import useCreateTemplate from 'hooks/services/templates/useCreateTemplate';
import TemplateType from 'crono-fe-common/types/enums/templateType';
import {
  styled,
  Tooltip as MUITooltip,
  tooltipClasses,
  TooltipProps,
} from '@mui/material';
import { useAuth } from 'context/auth';
import { useAnalytics } from 'context/analytics';
import { colors } from 'crono-fe-common/theme';
import useGetExternalProperty from 'hooks/services/externalProperty/useGetExternalProperty';
import { TagInsert } from 'crono-fe-common/types/tagInsert';
import { getError, getNewOptionsWhenSelecting } from 'crono-fe-common/utils';
import { Constants } from 'crono-fe-common/constants/constants';
import { blobToBase64 } from 'utils/uiHelpers';
import TagsBoxShowAll from 'components/TagsBoxShowAll';
import { ExternalProperty } from 'crono-fe-common/types/externalProperty';
import LanguagePicker from 'components/LanguagePicker';
import CronoVoiceVisualizer from 'pages/accountDrawer/linkedinView/cronoVoiceVisualizer';
import Gleap from 'gleap';
import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Switch,
} from '@crono-one/design-system';
import ListDropdown from 'pages/lists/Lists/ListDropdown';
import { List, ListType } from 'crono-fe-common/types/list';

export const CustomTooltipVariables = styled(
  ({ className, ...props }: TooltipProps) => (
    <MUITooltip
      arrow={props.arrow ? props.arrow : false}
      {...props}
      classes={{ popper: className }}
    />
  ),
)(() => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: 'white',
    color: 'rgba(0, 0, 0, 0.87)',
    position: 'relative',
    maxWidth: 350,
    border: `1px solid ${colors.grey4}`,
    borderRadius: 8,
    padding: 0,
    WebkitBoxShadow: '0px 4px 8px 0px rgba(0, 0, 0, 0.10)',
    MozBoxShadow: '0px 4px 8px 0px rgba(0, 0, 0, 0.10)',
    OBoxShadow: '0px 4px 8px 0px rgba(0, 0, 0, 0.10)',
    BoxShadow: '0px 4px 8px 0px rgba(0, 0, 0, 0.10)',
  },
}));

interface IProps {
  close: () => void;
  onCreated?: (template: Template) => void;
}

const TemplateManagement = ({ close, onCreated }: IProps) => {
  const { user } = useAuth();
  const analytics = useAnalytics();
  const [selectedList, setSelectedList] = useState<List | null>(null);
  const [shared, setShared] = useState(true);
  const [duration, setDuration] = useState<number>(0);
  const [recordedBlob, setRecordedBlob] = useState<Blob | null>(null);
  const [tagSelected, setTagSelected] = useState<TagInsert[]>([]);

  const {
    isSuccess: isSuccessCreate,
    data: dataCreate,
    error: errorCreateTemplate,
    mutate: createTemplate,
    isLoading: isCreateTemplateLoading,
  } = useCreateTemplate();

  useEffect(() => {
    if (!isSuccessCreate) return;
    const createdTemplate = dataCreate?.data.data;
    if (createdTemplate && onCreated) onCreated(createdTemplate);
    Gleap.trackEvent('create_template');
    close();
  }, [isSuccessCreate]);

  const { data: externalPropertyTags } = useGetExternalProperty({
    tableType: null,
    onlyTag: true,
  });

  const formik = useFormik<TemplateInputs>({
    initialValues: {
      ...initialValues,
      type: TemplateType.VOICE_NOTE,
      language:
        user?.userPreferences?.templateLanguage &&
        Constants.templateLanguages.includes(
          user.userPreferences.templateLanguage,
        )
          ? user.userPreferences.templateLanguage === 'all'
            ? 'en'
            : user.userPreferences.templateLanguage
          : Constants.templateLanguages[0],
    },
    validationSchema,
    onSubmit: async (values) => {
      const base64Recording = recordedBlob
        ? await blobToBase64(recordedBlob)
        : undefined;

      createTemplate({
        ...values,
        subject: '',
        content: '',
        attachments: [],
        attachmentIds: [],
        type: TemplateType.VOICE_NOTE,
        shared,
        templateTags: tagSelected,
        recordingDuration: base64Recording ? Math.round(duration) : undefined,
        recording: base64Recording,
        listId: selectedList?.id || null,
      });

      analytics?.track('create-template', {});
    },
  });

  const handleClickTag = (
    option: string | null,
    externalProperty: ExternalProperty,
  ) => {
    const externalPropertyId = externalProperty.id;
    const newFilters = [...tagSelected];

    const newValueOption = getNewOptionsWhenSelecting({
      currentExternalValues: newFilters,
      externalPropertyId,
      option: option,
      valueType: externalProperty.valueType,
    });
    const newExternalValue: TagInsert = {
      externalPropertyId: externalPropertyId,
      value: newValueOption!,
    };
    const oldTag = tagSelected.find(
      (tag) => tag.externalPropertyId === externalPropertyId,
    );
    if (oldTag && (oldTag?.value === option || option === null)) {
      newFilters.splice(
        newFilters.findIndex(
          (tag) => tag.externalPropertyId === externalPropertyId,
        ),
        1,
      );
    } else if (oldTag) {
      newFilters.splice(
        newFilters.findIndex(
          (tag) => tag.externalPropertyId === externalPropertyId,
        ),
        1,
        newExternalValue,
      );
    } else {
      newFilters.push(newExternalValue);
    }

    setTagSelected(newFilters);
  };

  useConditionalSnackBar([
    {
      condition: !!errorCreateTemplate,
      message: getError(errorCreateTemplate) ?? 'Error during create template',
      severity: 'error',
    },
    {
      condition: isSuccessCreate,
      message: 'Successfully created template',
      severity: 'success',
    },
  ]);

  const templateNameFieldRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    templateNameFieldRef.current?.focus();
  }, []);

  const isSaveDisabled = useMemo(
    () => !formik.values.title || !recordedBlob,
    [formik.values.title, recordedBlob],
  );

  return (
    <Dialog open onOpenChange={close}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Voice note</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <form
            onSubmit={formik.handleSubmit}
            className="flex flex-col gap-4.5 pb-5"
          >
            <div className="flex items-center mt-4 -mb-2.5">
              <CronoVoiceVisualizer
                exportDuration={setDuration}
                exportRecording={setRecordedBlob}
              />
            </div>

            <div className="flex flex-col gap-2">
              <label
                htmlFor="title"
                className="text-sm font-semibold text-gray-900 leading-4.5"
              >
                Template name*:
              </label>
              <Input
                id="title"
                name="title"
                placeholder="Template name"
                ref={templateNameFieldRef}
                value={formik.values.title}
                onChange={formik.handleChange}
              />
              {formik.touched.title && formik.errors.title && (
                <span className="text-xs text-red-600">
                  {formik.errors.title}
                </span>
              )}
            </div>

            <div className="w-full flex flex-col gap-1.5">
              <span className="text-sm font-semibold">Select language:*</span>
              <LanguagePicker
                language={formik.values.language ?? ''}
                onLanguageChange={(language) =>
                  formik.setFieldValue('language', language)
                }
                triggerClassName="w-full"
              />
            </div>

            <div className="relative flex flex-col gap-2">
              <p className="text-sm leading-6 font-semibold">Add to list:</p>
              <ListDropdown
                selectedList={selectedList}
                setSelectedList={setSelectedList}
                listType={ListType.Template}
                autoFillList
                inset="40% auto auto auto"
              />
            </div>

            {externalPropertyTags?.data?.data.length !== 0 && (
              <div className="flex flex-col gap-1.5">
                <span className="text-sm font-semibold">Add tags:</span>
                <div className="flex w-full flex-wrap gap-2">
                  <TagsBoxShowAll
                    type="AccountAndProspect"
                    currentSituationTags={tagSelected.map((tag) => ({
                      externalPropertyId: tag.externalPropertyId,
                      option: tag.value,
                    }))}
                    handleClickTag={handleClickTag}
                  />
                </div>
              </div>
            )}
          </form>
        </DialogBody>
        <DialogFooter>
          <label className="flex items-center">
            <span className="text-sm font-medium leading-4.5 text-gray-600">
              Private
            </span>
            <Switch
              checked={!shared}
              onCheckedChange={() => setShared((oldValue) => !oldValue)}
              className="ml-2"
            />
          </label>

          <div className="flex gap-3 ml-auto">
            <Button
              variant="outline"
              size="lg"
              disabled={isCreateTemplateLoading}
              onClick={() => close()}
            >
              Cancel
            </Button>
            <Button
              loading={isCreateTemplateLoading}
              size="lg"
              onClick={() => formik.handleSubmit()}
              disabled={isSaveDisabled}
            >
              Create
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default TemplateManagement;
